import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useParams, useNavigate } from "react-router-dom";

const EmployeeView = () => {
  const { id } = useParams();
  const URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const response = await axios.get(`${URL}/employees/${id}`, {
          withCredentials: true,
        });        
        if (response.data && response.data.data) {
          setEmployee(response.data.data);
        } else {
          toast.error("ไม่พบข้อมูลพนักงาน");
        }
      } catch (error) {
        toast.error("ไม่สามารถดึงข้อมูลพนักงานได้");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [URL, id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-700">ไม่พบข้อมูลพนักงาน</p>
        <button
          onClick={() => navigate("/panel/employee")}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          ยกเลิก
        </button>
      </div>
    );
  }

  const getGenderText = (gender) => {
    return gender === 0 ? "ชาย" : gender === 1 ? "หญิง" : "-";
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">การจัดการข้อมูลพนักงาน</h2>
      <hr className="mb-4"/>

      <nav className="flex mb-8" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li className="inline-flex items-center">
            <a href="/panel/employee" className="inline-flex items-center font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white">
              <svg className="w-3 h-3 me-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z"/>
              </svg>
              ข้อมูลพนักงาน
            </a>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
              </svg>
              <span className="ms-1 font-medium text-gray-500 md:ms-2 dark:text-gray-400">ดูข้อมูล</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold text-lg mb-4 text-gray-700 border-b pb-2">ข้อมูลส่วนตัว</h3>
            
            <div className="mb-4">
              <p className="text-sm font-bold text-gray-700 mb-1">ชื่อ-นามสกุล</p>
              <p className="text-gray-800">{employee.firstName} {employee.lastName}</p>
            </div>
            
            <div className="mb-4">
              <p className="text-sm font-bold text-gray-700 mb-1">อีเมล</p>
              <p className="text-gray-800">{employee.email || "-"}</p>
            </div>
            
            <div className="mb-4">
              <p className="text-sm font-bold text-gray-700 mb-1">เบอร์โทรศัพท์</p>
              <p className="text-gray-800">{employee.mobilePhone || "-"}</p>
            </div>
            
            <div className="mb-4">
              <p className="text-sm font-bold text-gray-700 mb-1">เพศ</p>
              <p className="text-gray-800">{getGenderText(employee.gender)}</p>
            </div>
            
            <div className="mb-4">
              <p className="text-sm font-bold text-gray-700 mb-1">ตำแหน่ง</p>
              <p className="text-gray-800">{employee.positionName || "-"}</p>
            </div>
          </div>
          
          <div>
            {employee.imageFile && (
              <div>
                <h3 className="font-bold text-lg mb-4 text-gray-700 border-b pb-2">รูปโปรไฟล์</h3>
                <div className="flex justify-center">
                  <img
                    src={`${URL}/images/employee/${employee.imageFile}`}
                    alt="รูปโปรไฟล์"
                    className="rounded-lg shadow-lg max-h-64 object-contain"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={() => navigate("/panel/employee")}
            className="mr-4 px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
          >
            ยกเลิก
          </button>
          <button
            onClick={() => navigate(`/panel/employee/edit/${id}`)}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            แก้ไขข้อมูล
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeView;